import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DashboardCardWrapper } from '../../components/DashboardCardWrapper/dashboard-card-wrapper';
import { DashboardCardContentValue } from '../../components/DashboardCardContentValue/dashboard-card-content-value';
import { BarChart } from '../../components';
import { NoDataWidget } from '../../dashboard-basic-view/components/NoDataWidget/no-data-widget';
import { formatNumber, PLACEHOLDER } from '../../../reporting/components/portfolio-report';
import { ReportingPaths } from '../../../reporting/reporting.router';
import { PortfolioWidgetProps } from './portfolio-widget.types';

export function PortfolioCompositionWidget({ data, isLoading }: PortfolioWidgetProps) {
  const { t } = useTranslation('widget');
  const { t: tType } = useTranslation('type');

  const buildings = useMemo(() => data?.buildings ?? [], [data]);
  const buildingCount = data?.summary?.buildingCount ?? buildings.length;
  // BRA (grossAreaM2), not summary.totalAreaM2 — the latter is the sum of *rentable child spaces*,
  // which understates the portfolio whenever buildings have unregistered space. Summing BRA keeps
  // this figure equal to the bars below and to "Totalt areal (BRA)" on the portfolio-overview report.
  const totalArea = useMemo(() => buildings.reduce((sum, b) => sum + (b.grossAreaM2 || 0), 0), [buildings]);

  const areaByType = useMemo(() => {
    const translateType = (type: string): string =>
      type === 'unknown'
        ? t('portfolioComposition.unknownType')
        : tType(`BuildingType.${type}`, { defaultValue: type });

    const map = new Map<string, number>();
    buildings.forEach((b) => {
      const key = b.type ?? 'unknown';
      map.set(key, (map.get(key) ?? 0) + (b.grossAreaM2 || 0));
    });
    return Array.from(map.entries())
      .map(([type, value]) => ({ type: translateType(type), value }))
      .sort((a, b) => b.value - a.value);
  }, [buildings, t, tType]);

  if (!isLoading && buildingCount === 0)
    return <NoDataWidget title={t('portfolioComposition.title')} noDataText={t('portfolioComposition.noData')} />;

  return (
    <DashboardCardWrapper
      to={ReportingPaths.PortfolioOverview2}
      title={t('portfolioComposition.title')}
      tooltip={t('portfolioComposition.tooltip')}
      loading={isLoading || !data}
    >
      <DashboardCardContentValue value={formatNumber(buildingCount)} unit={t('portfolioComposition.buildingsUnit')}>
        <span className="flex items-end text-[0.86625rem] text-gray-300">
          {totalArea > 0 ? `${formatNumber(totalArea)} m²` : PLACEHOLDER}
        </span>
      </DashboardCardContentValue>
      <div className="w-full h-[200px]">
        <BarChart
          data={areaByType}
          indexBy="type"
          keys={['value']}
          colors="#3F98B4"
          axisBottom={{
            tickSize: 0,
            tickPadding: 8,
            tickRotation: areaByType.length > 3 ? -25 : 0,
          }}
        />
      </div>
    </DashboardCardWrapper>
  );
}
